import { DocumentNode, gql } from '@apollo/client'

/**
 * Table column id -> the `ListingMatch` selection that backs it, used to build
 * the export query from only the columns the user actually selected.
 *
 * Ids absent from this map are skipped by `buildProductExportQuery`. That is
 * deliberate for the column ids with no backing field on `ListingMatch`:
 * `fullLengthDoc` and `importCatalog` render action buttons and
 * `groupColExpander` is the grouping expander, so selecting them would fail
 * schema validation. The export already emits '' for them because the full
 * listing card query never returned them either.
 */
const EXPORT_FIELD_SELECTIONS: Record<string, string> = {
    assignedBPMUsers: `assignedBPMUsers {
                        profileInfo {
                            firstName
                            lastName
                        }
                    }`,
    assignedContactUsers: `assignedContactUsers {
                        profileInfo {
                            firstName
                            lastName
                        }
                    }`,
    assignedDeltekUsers: `assignedDeltekUsers {
                        profileInfo {
                            firstName
                            lastName
                        }
                    }`,
    canAutoGenerateProductSpecification: 'canAutoGenerateProductSpecification',
    categories: 'categories',
    contentLibrary: 'contentLibrary',
    description: 'description',
    displayNameWithFirmName: 'displayNameWithFirmName',
    familyNumber: 'familyNumber',
    firmDisplayName: 'firmDisplayName',
    firmId: 'firmId',
    firmLogoUrl: 'firmLogoUrl',
    firmName: 'firmName',
    hasManufacturerOptions: 'hasManufacturerOptions',
    hasProductDetailNotes: 'hasProductDetailNotes',
    isActive: 'isActive',
    lastUpdated: 'lastUpdated',
    listingId: 'listingId',
    model: 'model',
    partnershipLinksBIM: 'partnershipLinksBIM',
    partnershipLinksSM: 'partnershipLinksSM',
    partnershipLinksSwatchbox: 'partnershipLinksSwatchbox',
    partnershipLinksULE: 'partnershipLinksULE',
    productName: 'productName',
    productType: 'productType',
    sectionName: 'sectionName',
    specAgentProductTypeId: 'specAgentProductTypeId',
    submissionStatus: 'submissionStatus',
    type: 'type',
    updatedLastAiScrapeDate: 'updatedLastAiScrapeDate',
}

/**
 * `listingId` backs the XLSX product-name hyperlink, so every export query
 * requests it even when the user has not selected the Listing ID column.
 */
const ALWAYS_EXPORTED_COLUMN_IDS = ['listingId']

/**
 * Pulls the field names out of an orderBy clause
 * (`'sectionName,type desc'` -> `['sectionName', 'type']`). The export re-sorts
 * each fetched page client-side, so the fields it sorts on must be selected
 * even when they are not exported as columns.
 */
const getOrderByFieldNames = (orderBy?: string): string[] =>
    (orderBy ?? '')
        .split(',')
        .map((clause) => clause.trim().split(/\s+/)[0])
        .filter(Boolean)

/**
 * Builds a `listingSearch` query selecting only the fields the export needs,
 * rather than the full listing card the product table renders.
 *
 * `gql` memoizes by document string, so an unchanged column set yields the same
 * DocumentNode instance across renders -- which keeps Apollo's `useLazyQuery`
 * from re-subscribing on every render.
 */
export const buildProductExportQuery = (
    columnIds: string[],
    orderBy?: string
): DocumentNode => {
    const selections = Array.from(
        new Set(
            [
                ...ALWAYS_EXPORTED_COLUMN_IDS,
                ...columnIds,
                ...getOrderByFieldNames(orderBy),
            ]
                .map((columnId) => EXPORT_FIELD_SELECTIONS[columnId])
                .filter(Boolean)
        )
    )

    return gql`
        query productExportQuery(
            $manufacturerProductSearchInput: ManufacturerProductSearchInput!
        ) {
            listingSearch(input: $manufacturerProductSearchInput) {
                matches {
                    ${selections.join('\n                    ')}
                }
                count
            }
        }
    `
}
